import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Shuffle, ArrowRight, RotateCcw } from "lucide-react";
import { useLang } from "../../context/LanguageContext";

const shuffled = (arr) => [...arr].sort(() => Math.random() - 0.5);

export const MatchBlock = ({ block, onDone }) => {
  const { t } = useLang();
  const rights = useMemo(() => shuffled(block.pairs.map((p) => p.right)), [block]);
  const [pairs, setPairs] = useState({});
  const [activeLeft, setActiveLeft] = useState(null);

  const usedRights = Object.values(pairs);
  const complete = Object.keys(pairs).length === block.pairs.length;

  const pickRight = (right) => {
    if (!activeLeft || usedRights.includes(right)) return;
    setPairs({ ...pairs, [activeLeft]: right });
    setActiveLeft(null);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-dark p-7 sm:p-10" data-testid="learn-block-match">
      <span className="badge-lime inline-flex items-center gap-2"><Shuffle className="w-3.5 h-3.5" /> {t("learn.exercise")}</span>
      <h2 className="font-heading font-black text-xl sm:text-2xl text-[#0B1B3A] mt-5 leading-snug" data-testid="learn-match-prompt">{block.prompt}</h2>
      <p className="text-sm text-[#64748B] mt-3">{t("learn.matchHint")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-7">
        <div className="space-y-3">
          {block.pairs.map((p, i) => (
            <button
              key={p.left}
              data-testid={`learn-match-left-${i}`}
              onClick={() => setActiveLeft(p.left)}
              className={`w-full text-left rounded-[18px] border px-5 py-4 text-sm font-semibold transition-colors duration-200 ${
                activeLeft === p.left ? "border-[#1D4ED8] bg-[#EEF4FF]" : pairs[p.left] ? "border-[#CBD5E1] bg-[#F8FAFC]" : "border-[#E2E8F0] hover:border-[#1D4ED8]"
              }`}
            >
              <span className="text-[#0B1B3A]">{p.left}</span>
              {pairs[p.left] && <span className="block text-xs font-bold text-[#1D4ED8] mt-1">→ {pairs[p.left]}</span>}
            </button>
          ))}
        </div>
        <div className="space-y-3">
          {rights.map((right, i) => (
            <button
              key={right}
              data-testid={`learn-match-right-${i}`}
              disabled={usedRights.includes(right)}
              onClick={() => pickRight(right)}
              className={`w-full text-left rounded-[18px] border px-5 py-4 text-sm font-semibold transition-colors duration-200 ${
                usedRights.includes(right) ? "border-[#E2E8F0] text-[#CBD5E1]" : "border-[#E2E8F0] text-[#1E293B] hover:border-[#1D4ED8] hover:bg-[#EEF4FF]"
              }`}
            >
              {right}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-8">
        <button data-testid="learn-match-reset" onClick={() => { setPairs({}); setActiveLeft(null); }} className="btn-outline-light">
          <RotateCcw className="w-4 h-4" /> {t("learn.reset")}
        </button>
        <button data-testid="learn-match-submit" disabled={!complete} onClick={() => onDone(pairs)} className="btn-blue disabled:opacity-50">
          {t("learn.continue")} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default MatchBlock;
